import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ROLE_IDS } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export default function Register() {
  const { register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPw: '', city: '', roleId: ROLE_IDS.NORMAL, agreeTerms: false });
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const PLANS = [
    { roleId: ROLE_IDS.NORMAL, label: 'Normal', price: t('register.plan_free'), features: t('register.plan_normal_features'), color: 'border-gray-200' },
    { roleId: ROLE_IDS.VIP, label: 'VIP', price: '9,99€/mo', features: t('register.plan_vip_features'), color: 'border-apple-blue', highlight: true },
    { roleId: ROLE_IDS.SHOP, label: 'Shop', price: '24,99€/mo', features: t('register.plan_shop_features'), color: 'border-apple-purple' },
  ];

  const validateStep1 = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('register.err_name');
    if (!form.email.includes('@')) e.email = t('register.err_email');
    if (form.password.length < 6) e.password = t('register.err_pw');
    if (form.password !== form.confirmPw) e.confirmPw = t('register.err_confirm');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    setLoading(true);
    try {
      await register({
        userNameSurname: form.name,
        email: form.email,
        password: form.password,
        city: form.city,
        role: form.roleId,
      });
      navigate('/');
    } catch (err) {
      setApiError(err?.response?.data?.message || t('register.default_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-apple-blue rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">B</span>
          </div>
          <h1 className="text-2xl font-bold text-apple-gray-dark">{t('register.title')}</h1>
          <p className="text-apple-gray-mid text-sm mt-1">{t('register.subtitle')}</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2].map(s => (
            <div key={s} className={`w-2 h-2 rounded-full transition-all ${step >= s ? 'bg-apple-blue w-6' : 'bg-gray-200'}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="card p-8 animate-fade-in">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('register.name')} *</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Max Mustermann" className="input-field" />
                {errors.name && <p className="text-xs text-apple-red mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('register.email')} *</label>
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="ornek@email.de" className="input-field" />
                {errors.email && <p className="text-xs text-apple-red mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('register.password')} *</label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} placeholder={t('register.pw_placeholder')} className="input-field pr-10" />
                  <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-apple-gray-mid">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-apple-red mt-1">{errors.password}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('register.confirm_pw')} *</label>
                <input type="password" value={form.confirmPw} onChange={e => set('confirmPw', e.target.value)} placeholder={t('register.confirm_placeholder')} className="input-field" />
                {errors.confirmPw && <p className="text-xs text-apple-red mt-1">{errors.confirmPw}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-apple-gray-dark mb-1.5">{t('register.city')}</label>
                <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Berlin" className="input-field" />
              </div>
              <button onClick={() => validateStep1() && setStep(2)} className="btn-primary w-full py-3 text-base font-semibold">
                {t('register.next')}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-in">
            {apiError && (
              <div className="flex items-center gap-2 bg-red-50 text-apple-red text-sm p-3 rounded-xl mb-4">
                <AlertCircle className="w-4 h-4 shrink-0" /> {apiError}
              </div>
            )}

            <div className="space-y-4 mb-6">
              {PLANS.map(plan => (
                <button
                  key={plan.roleId}
                  onClick={() => set('roleId', plan.roleId)}
                  className={`w-full text-left border-2 rounded-2xl p-5 transition-all ${form.roleId === plan.roleId ? (plan.highlight ? 'border-apple-blue bg-blue-50/50' : 'border-apple-gray-dark bg-gray-50') : plan.color + ' hover:border-gray-400'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-apple-gray-dark">{plan.label}</span>
                    <div className="flex items-center gap-2">
                      <span className={`font-bold ${plan.highlight ? 'text-apple-blue' : 'text-apple-gray-dark'}`}>{plan.price}</span>
                      {form.roleId === plan.roleId && <div className="w-5 h-5 rounded-full bg-apple-blue flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                    </div>
                  </div>
                  <ul className="space-y-1">
                    {plan.features.map(f => <li key={f} className="text-xs text-apple-gray-mid flex items-center gap-1.5"><Check className="w-3 h-3 text-apple-green shrink-0" />{f}</li>)}
                  </ul>
                </button>
              ))}
            </div>

            <div className="card p-5 mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={form.agreeTerms} onChange={e => set('agreeTerms', e.target.checked)} className="mt-0.5 accent-apple-blue" />
                <span className="text-sm text-apple-gray-mid">
                  {t('register.terms_prefix')}
                  <Link to="#" className="text-apple-blue hover:underline">{t('register.terms_link1')}</Link>
                  {' '}{t('register.terms_and')}{' '}
                  <Link to="#" className="text-apple-blue hover:underline">{t('register.terms_link2')}</Link>
                  {t('register.terms_suffix')}
                </span>
              </label>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="btn-secondary flex-1 py-3">{t('register.back')}</button>
              <button onClick={handleSubmit} disabled={!form.agreeTerms || loading} className="btn-primary flex-1 py-3 font-semibold disabled:opacity-60">
                {loading ? t('register.submitting') : t('register.submit')}
              </button>
            </div>
          </div>
        )}

        <p className="text-center text-sm text-apple-gray-mid mt-6">
          {t('register.has_account')} <Link to="/login" className="text-apple-blue font-medium hover:underline">{t('register.login_link')}</Link>
        </p>
      </div>
    </div>
  );
}
